// System claims (04 §5.8, visibility §3.6) and claim events (§7.4).
import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from './common.js';

const credits = z.number().int().nonnegative();

export const CLAIM_STATUSES = [
  'pending',
  'active',
  'fulfilled',
  'cancelled',
  'expired',
  'invalidated',
] as const;
export const claimStatusSchema = z.enum(CLAIM_STATUSES);

export const claimQuoteQuerySchema = z.object({ systemId: idSchema });

export const claimQuoteSchema = z.strictObject({
  costCredits: credits,
  claimWeightN: z.number().int().positive(),
  activatesAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
  /** How long after placement the claim activates / expires, at the galaxy speed (for display). */
  activatesAfterMs: z.number().int().nonnegative(),
  expiresAfterMs: z.number().int().nonnegative(),
});
export type ClaimQuote = z.infer<typeof claimQuoteSchema>;

export const placeClaimRequestSchema = z.strictObject({
  systemId: idSchema,
  /** The quoted cost; a different current cost fails with `409 COST_CHANGED`. */
  expectedCostCredits: credits,
});
export type PlaceClaimRequest = z.infer<typeof placeClaimRequestSchema>;

/** The claimant and the claimant's intel audience see everything. */
export const claimantClaimSchema = z.strictObject({
  view: z.literal('claimant'),
  id: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  playerId: idSchema,
  status: claimStatusSchema,
  creditsPaid: credits,
  claimWeightN: z.number().int().positive(),
  placedAt: isoDateTimeSchema,
  activatesAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
  resolvedAt: isoDateTimeSchema.nullable(),
  creditsRefunded: credits,
  /** What cancelling now would refund (pending: part of `creditsPaid`, active: 0); 0 once closed. */
  cancelRefundCredits: credits,
});
export type ClaimantClaim = z.infer<typeof claimantClaimSchema>;

/** The owner of the claimed system and the owner's alliance: who and when, never the Credits paid. */
export const ownerClaimSchema = z.strictObject({
  view: z.literal('owner'),
  id: idSchema,
  systemId: idSchema,
  systemName: z.string(),
  claimant: z.strictObject({
    playerId: idSchema,
    displayName: z.string(),
    allianceTag: z.string().nullable(),
  }),
  status: claimStatusSchema,
  placedAt: isoDateTimeSchema,
  activatesAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
});
export type OwnerClaim = z.infer<typeof ownerClaimSchema>;

export const claimSchema = z.discriminatedUnion('view', [claimantClaimSchema, ownerClaimSchema]);
export type Claim = z.infer<typeof claimSchema>;

export const placeClaimResponseSchema = z.strictObject({
  claim: claimantClaimSchema,
  credits,
});
export type PlaceClaimResponse = z.infer<typeof placeClaimResponseSchema>;

export const cancelClaimResponseSchema = z.strictObject({
  claim: claimantClaimSchema,
  refundedCredits: credits,
});
export type CancelClaimResponse = z.infer<typeof cancelClaimResponseSchema>;

export const claimListQuerySchema = z.object({
  scope: z.enum(['mine', 'incoming']).default('mine'),
});

export const claimListResponseSchema = z.strictObject({ items: z.array(claimSchema) });
export type ClaimListResponse = z.infer<typeof claimListResponseSchema>;

export const claimPlacedSchema = z.strictObject({
  claimId: idSchema,
  systemId: idSchema,
  claimant: z.strictObject({
    playerId: idSchema,
    displayName: z.string(),
    allianceTag: z.string().nullable(),
  }),
  activatesAt: isoDateTimeSchema,
  expiresAt: isoDateTimeSchema,
});

export const claimActivatedSchema = z.strictObject({ claimId: idSchema, systemId: idSchema });

export const claimResolvedSchema = z.strictObject({
  claimId: idSchema,
  systemId: idSchema,
  status: claimStatusSchema,
  refundedCredits: credits,
});
